/**
 * Org Detail / Overview tab (Task 53c.2).
 *
 * Pulls organisation data already loaded by the OrgDetail parent page
 * (via React Router's Outlet context — no extra fetch).
 *
 * What lives here in 53c.2: core metadata (name, billing email, plan,
 * billing method, enabled flag, project counts) + Stripe linkage flags.
 *
 * Things deliberately not here: Stripe subscription state, invoice list,
 * archive controls. Those live in the Plan & Billing tab once 53c.2b
 * lands. The legacy slide-over modal still surfaces them from the org
 * list for now.
 */
import { useOutletContext } from 'react-router-dom';
import type { OrgDetailContext } from '../OrgDetail';

export default function Overview() {
  const { organisation } = useOutletContext<OrgDetailContext>();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
      <Card title="Identification">
        <Row label="Org ID" value={<code className="text-xs">{organisation.id}</code>} />
        <Row label="Name" value={organisation.name} />
        <Row label="Billing email" value={organisation.billingEmail} />
        {organisation.contactName && <Row label="Contact name" value={organisation.contactName} />}
        {organisation.contactEmail && (
          <Row label="Contact email" value={organisation.contactEmail} />
        )}
      </Card>

      <Card title="Plan">
        <Row label="Plan ID" value={<code className="text-xs">{organisation.planId}</code>} />
        {organisation.billingMethod && (
          <Row label="Billing method" value={organisation.billingMethod} />
        )}
        {organisation.planStatus && <Row label="Plan status" value={organisation.planStatus} />}
        {organisation.planAssignedAt && (
          <Row label="Assigned" value={formatDate(organisation.planAssignedAt)} />
        )}
        {organisation.planActivatedAt && (
          <Row label="Activated" value={formatDate(organisation.planActivatedAt)} />
        )}
      </Card>

      <Card title="Usage">
        <Row
          label="Projects"
          value={`${organisation.currentProjects ?? 0} / ${organisation.maxProjects ?? '—'}`}
        />
      </Card>

      <Card title="Stripe linkage">
        <Row
          label="Customer ID"
          value={
            organisation.stripeCustomerId ? (
              <code className="text-xs">{organisation.stripeCustomerId}</code>
            ) : (
              <span className="text-base-content/40">Not linked</span>
            )
          }
        />
        <Row
          label="Subscription ID"
          value={
            organisation.stripeSubscriptionId ? (
              <code className="text-xs">{organisation.stripeSubscriptionId}</code>
            ) : (
              <span className="text-base-content/40">None</span>
            )
          }
        />
      </Card>

      {organisation.address && (
        <Card title="Address" className="md:col-span-2">
          <Row label="Street" value={organisation.address.street} />
          <Row label="City" value={organisation.address.city} />
          <Row label="Postal code" value={organisation.address.postalCode} />
          <Row label="Country" value={organisation.address.country} />
        </Card>
      )}
    </div>
  );
}

function Card({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`bg-base-100 shadow rounded-lg p-6 ${className ?? ''}`}>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/60 mb-3">
        {title}
      </h2>
      <dl className="space-y-2">{children}</dl>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-sm text-base-content/70">{label}</dt>
      <dd className="text-sm text-base-content text-right break-all">{value}</dd>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
